<template>
    <div class="editable-groups">
        <template v-if="editableGroupIds.length">
            <EditableGroup
                v-for="groupName in editableGroupIdsWithVisibleEditables"
                :key="groupName"
                :group-name="groupName"
                @add="$emit(EditableGroupEvent.Add, $event)"
                @duplicate="$emit(EditableGroupEvent.Duplicate, $event)"
                @update="$emit(EditableGroupEvent.Update, $event)"
                @remove="$emit(EditableGroupEvent.Remove, $event)"
                @edit="$emit(EditableGroupEvent.Edit, $event)"
                @importFeed="$emit(EditableGroupEvent.ImportFeed, $event)"
                @refreshFeed="$emit(EditableGroupEvent.RefreshFeed, $event)"
                @deleteFeed="$emit(EditableGroupEvent.DeleteFeed, $event)"
            />

            <div>
                <div
                    v-for="publisherPlatformGroupName in editableGroupsOfPublisherPlatform"
                    :key="publisherPlatformGroupName"
                >
                    <EditableGroup
                        v-for="groupName in getEditableGroupIdsForPublisherPlatform(publisherPlatformGroupName)"
                        :key="groupName"
                        :group-name="groupName"
                        @add="$emit(EditableGroupEvent.Add, $event)"
                        @duplicate="$emit(EditableGroupEvent.Duplicate, $event)"
                        @update="$emit(EditableGroupEvent.Update, $event)"
                        @remove="$emit(EditableGroupEvent.Remove, $event)"
                        @edit="$emit(EditableGroupEvent.Edit, $event)"
                        @importFeed="$emit(EditableGroupEvent.ImportFeed, $event)"
                        @refreshFeed="$emit(EditableGroupEvent.RefreshFeed, $event)"
                        @deleteFeed="$emit(EditableGroupEvent.DeleteFeed, $event)"
                    />
                </div>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
import EditableGroup from "@/components/Campaign/Editables/EditableGroup.vue";

import { EditableGroupEvent } from "@/enums/editables";
import type { Editable } from "hoxton-types";

export default {
    name: "EditableGroupList",
    components: {
        EditableGroup
    },

    inheritAttrs: false,

    emits: [
        EditableGroupEvent.Add,
        EditableGroupEvent.Duplicate,
        EditableGroupEvent.Edit,
        EditableGroupEvent.Remove,
        EditableGroupEvent.Update,
        EditableGroupEvent.ImportFeed,
        EditableGroupEvent.RefreshFeed,
        EditableGroupEvent.DeleteFeed
    ],

    data() {
        return {
            EditableGroupEvent
        };
    },

    computed: {
        campaignId() {
            return this.$route.params.campaignId as string;
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        editableGroupIds() {
            return [...this.$store.state.campaign.normalized.editableGroupIds];
        },

        editableGroupsOfPublisherPlatform() {
            const editableGroups = new Set<string>();
            this.editableGroupIds.forEach(editableGroupId => {
                this.$store.state.campaign.normalized.editableGroups[editableGroupId].editables.forEach(editableId => {
                    const editableGroup = this.$store.state.campaign.normalized.editables[editableId];
                    if (editableGroup.publisherPlatform) {
                        editableGroups.add(editableGroup.publisherPlatform);
                    }
                });
            });

            return [...editableGroups];
        },

        editableGroupIdsWithVisibleEditables() {
            if (
                this.$auth.userCan(this.$auth.Actions.CanManageRestrictedEditables, {
                    campaignId: this.campaignId,
                    clientId: this.clientId
                })
            ) {
                const showEditableGroupByCondition = (editable: Editable) => !editable.publisherPlatform;
                return this.displayEditableGroup(showEditableGroupByCondition);
            }

            const showEditableGroupByCondition = (editable: Editable) =>
                !editable.restricted && !editable.publisherPlatform;
            return this.displayEditableGroup(showEditableGroupByCondition);
        }
    },

    methods: {
        displayEditableGroup(showEditableGroupByCondition: (editable: Editable) => boolean) {
            // filter all editable groups that is not related to any publisher platform
            return this.editableGroupIds.filter(editableGroupId => {
                return this.$store.state.campaign.normalized.editableGroups[editableGroupId].editables.some(
                    editableId => {
                        const editable = this.$store.state.campaign.normalized.editables[editableId];
                        return showEditableGroupByCondition(editable);
                    }
                );
            });
        },

        getEditableGroupIdsForPublisherPlatform(platformName: string) {
            const showEditableGroupByCondition = (editable: Editable) => editable.publisherPlatform === platformName;
            return this.displayEditableGroup(showEditableGroupByCondition);
        }
    }
};
</script>

<style scoped>
.editable-groups {
    padding-bottom: 80px;
}
</style>
